'use client';

import {
  Button,
  ButtonGroup,
  Checkbox,
  EmptyState,
  Flex,
  Heading,
  OverviewCell,
  CardPagination,
  CardTable,
  Select,
  CellCheckbox,
  Text,
} from '@/_components';
import { useMemo, useContext, type FC } from 'react';
import {
  ColumnDef,
  getSortedRowModel,
  getCoreRowModel,
  useTable,
  OnChangeFn,
} from '@tanstack/react-table';
import { OVERVIEW_TABLE_HEADER_MAP } from '@/_constants/overview';
import { PAGINATION_OPTIONS } from '@/_constants/tables';
import { OverviewContext } from '@/_contexts/admin/OverviewProvider';
import type { OverviewTableProps } from './OverviewTable.types';
import type { OverviewColumnType } from '@/_types/overview';
import { QuestionnaireContext } from '@/_contexts/admin/QuestionnaireProvider';
import { LayoutContext } from '@/_contexts/LayoutProvider';
import { OverviewTableFilteredEmpty } from './components/OverviewTableFilteredEmpty';
import { getClasses } from '@/_utils/classnames';
import { getCookie, type CookieValueTypes } from 'cookies-next/client';
import { OVERVIEW_FIRM_ROLE_ID_COOKIE } from '@/_constants/cookies';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';

const COLUMN_SHOWN_WITH_AI: Array<OverviewColumnType> = [
  'select',
  'client',
  'name',
  'status',
  'actions',
];

const SORTING_ENABLED_COLUMNS: Array<OverviewColumnType> = [
  'client',
  'name',
  'status',
  'last_activity',
  'due_date',
  'documents',
  'firm_custom_status_id',
  'notes',
  'submitted_at',
  'first_doc_uploaded_at',
];

export const OverviewTable: FC<OverviewTableProps> = ({
  isLoading,
  selectedQuestionnaires,
  setSelectedQuestionnaires,
}) => {
  const {
    isFiltering,
    columns,
    pagination,
    sorting,
    setSorting,
    setPagination,
    resetFilters,
  } = useContext(OverviewContext);
  const { setSending, questionnaires, questionnaireCount, loadQuestionnaires } =
    useContext(QuestionnaireContext);
  const { firmRoles } = useContext(FirmSettingsContext);
  const { isAssistantOpen, setFilterContent } = useContext(LayoutContext);

  const visibleColumns = useMemo<Array<OverviewColumnType>>(() => {
    if (!isAssistantOpen) return columns;

    const availableColumns = new Set(columns);

    const aiColumns = COLUMN_SHOWN_WITH_AI.reduce<Array<OverviewColumnType>>(
      (acc, column) => {
        if (column === 'entity') {
          if (availableColumns.has('entity')) {
            acc.push('entity');
          } else if (availableColumns.has('client')) {
            acc.push('client');
          }

          return acc;
        }

        if (availableColumns.has(column)) {
          acc.push(column);
        }

        return acc;
      },
      [],
    );

    return aiColumns.length ? aiColumns : columns;
  }, [columns, isAssistantOpen]);

  const isEmpty: boolean = useMemo(
    () => !questionnaires?.length && !isLoading,
    [isLoading, questionnaires],
  );

  const onRefresh = () => loadQuestionnaires();

  const tableColumns = useMemo<ColumnDef<any>[]>(() => {
    const mapped = visibleColumns.map((column) => {
      return {
        accessorKey: column,
        cell: (info: any) => info.getValue(),
        header: () => <span>{column}</span>,
      };
    });

    return mapped;
  }, [visibleColumns]);

  const table = useTable({
    columns: tableColumns,
    data: questionnaires,
    enableSortingRemoval: false,
    rowCount: questionnaireCount,
    // debugTable: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    // manualPagination: true,
    manualSorting: true,
    state: {
      sorting,
      pagination,
    },
    onPaginationChange: setPagination as OnChangeFn<any>,
    autoResetPageIndex: false,
  });

  const onResetFilters = () => {
    resetFilters();
    setFilterContent(null);
  };

  const toggleQuestionnaireSelection = (
    questionnaire?: QuestionnaireIndexSerializer,
  ) => {
    const isSelected: boolean = !!selectedQuestionnaires?.find(
      (q) => q.id === questionnaire?.id,
    )?.id;

    const newSelectedQuestionnaires: Array<QuestionnaireIndexSerializer> =
      isSelected
        ? selectedQuestionnaires?.filter((q) => q.id !== questionnaire?.id)
        : questionnaire
          ? selectedQuestionnaires.concat(questionnaire)
          : selectedQuestionnaires;

    return setSelectedQuestionnaires(newSelectedQuestionnaires);
  };

  const isAllVisibleSelected: boolean = useMemo(
    () =>
      questionnaires?.every(
        (q) => selectedQuestionnaires?.find((sq) => sq.id === q.id)?.id,
      ),
    [selectedQuestionnaires, questionnaires],
  );

  const onToggleSelectVisible = () => {
    if (isAllVisibleSelected) {
      const newSelectedQuestionnaires: Array<QuestionnaireIndexSerializer> =
        selectedQuestionnaires?.filter(
          (selectedQuestionnaire) =>
            !questionnaires.some(
              (visibleQuestionnaire) =>
                visibleQuestionnaire.id === selectedQuestionnaire.id,
            ),
        );

      setSelectedQuestionnaires(newSelectedQuestionnaires);
    } else {
      const newSelectedQuestionnaires: Array<QuestionnaireIndexSerializer> = [
        ...new Set([...selectedQuestionnaires, ...questionnaires]),
      ];

      setSelectedQuestionnaires(newSelectedQuestionnaires);
    }
  };

  const selectedFirmRoleId: CookieValueTypes = getCookie(
    OVERVIEW_FIRM_ROLE_ID_COOKIE,
  );

  const selectedFirmRoleName: string | undefined = useMemo(() => {
    return firmRoles?.find(
      (fr) => fr.id === parseInt(selectedFirmRoleId as string),
    )?.role_name;
  }, [selectedFirmRoleId, firmRoles]);

  const start = pagination.pageIndex * pagination.pageSize + 1;
  const end = Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    questionnaireCount,
  );
  const rowRange = `${start}-${end}`;

  if (isEmpty && !isFiltering) {
    return (
      <CardTable isLoading={false}>
        <EmptyState description="No questionnaires found" icon="questionnaire">
          <ButtonGroup align="center">
            <Button
              intent="primary"
              iconLeft="plus"
              onClick={() => setSending(true)}
              value="Send Questionnaire"
            />
            <Button
              intent="secondary"
              iconRight="refresh"
              isIconOnly={true}
              onClick={onRefresh}
              value="Refresh"
            />
          </ButtonGroup>
        </EmptyState>
      </CardTable>
    );
  }

  if (isEmpty && isFiltering) {
    return (
      <CardTable isLoading={false}>
        <OverviewTableFilteredEmpty onResetFilters={onResetFilters} />
      </CardTable>
    );
  }

  return (
    <>
      <CardTable
        hasSelection={!!selectedQuestionnaires?.length}
        isLoading={isLoading}
      >
        <table
          className="sorabanTable"
          data-testid="OverviewTable-collect-table"
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => {
                  const isLastColumn: boolean =
                    index === headerGroup.headers.length - 1;
                  const isSortingEnabled: boolean =
                    SORTING_ENABLED_COLUMNS.includes(
                      header.column.id as OverviewColumnType,
                    );
                  const thClasses: string = getClasses({
                    sorabanTable__fixedColumn: isLastColumn,
                  });

                  return (
                    <th
                      key={header.id}
                      data-sorted={header.column.getIsSorted()}
                      colSpan={header.colSpan}
                      className={thClasses}
                      style={{
                        width:
                          header.id === 'select' ? '32px' : header.getSize(),
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Flex
                          isYCentered={true}
                          isXCentered={isLastColumn}
                          gap={2}
                          onClick={
                            isSortingEnabled
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          {header.id === 'select' ? (
                            <CellCheckbox isHeader={true}>
                              <Checkbox
                                label="Select"
                                isLabelHidden={true}
                                isChecked={isAllVisibleSelected}
                                onChange={onToggleSelectVisible}
                              />
                            </CellCheckbox>
                          ) : (
                            <Heading level={7} isSameLine={true}>
                              {
                                OVERVIEW_TABLE_HEADER_MAP[
                                  header.column.id as OverviewColumnType
                                ]
                              }
                              {header.column.id === 'firm_role' &&
                                selectedFirmRoleName && (
                                  <> ({selectedFirmRoleName})</>
                                )}
                            </Heading>
                          )}
                          {isSortingEnabled && (
                            <ButtonGroup isSameLine={true}>
                              {{
                                asc: (
                                  <Button
                                    value="Sort Ascending"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-up"
                                  />
                                ),
                                desc: (
                                  <Button
                                    value="Sort Descending"
                                    intent="ghost"
                                    size="tiny"
                                    isIconOnly={true}
                                    iconLeft="arrow-down"
                                  />
                                ),
                              }[header.column.getIsSorted() as string] ?? (
                                <Button
                                  value="Sort"
                                  intent="ghost"
                                  size="tiny"
                                  isIconOnly={true}
                                  iconLeft="minus"
                                  withTooltip
                                  tooltipPlacement="bottom"
                                />
                              )}
                            </ButtonGroup>
                          )}
                        </Flex>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => {
              return (
                <tr key={row.id} data-testid="OverviewTable-questionnaire-row">
                  {row.getVisibleCells().map((cell, cellIndex) => {
                    const questionnaire: QuestionnaireIndexSerializer =
                      cell?.row?.original;
                    const isLastColumn: boolean =
                      cellIndex === row.getVisibleCells().length - 1;
                    const isTop: boolean = Boolean(
                      rowIndex / table.getRowModel().rows.length >= 0.5,
                    );

                    const isSelected: boolean = !!selectedQuestionnaires?.find(
                      (q) => q.id === questionnaire.id,
                    )?.id;

                    return (
                      <OverviewCell
                        isSelected={isSelected}
                        key={cell.id}
                        isTop={isTop}
                        isLastColumn={isLastColumn}
                        type={cell.column.id as OverviewColumnType}
                        questionnaire={questionnaire}
                        setSelectedQuestionnaires={setSelectedQuestionnaires}
                        toggleQuestionnaireSelection={
                          toggleQuestionnaireSelection
                        }
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardTable>
      <CardPagination>
        <Flex gap={4} isColumn={true}>
          <ButtonGroup align="center">
            <Button
              intent="ghost"
              size="tiny"
              value="<<"
              isIconOnly={true}
              iconLeft="caret-double-left"
              onClick={() => table.firstPage()}
              isDisabled={!table.getCanPreviousPage()}
            />
            <Button
              intent="ghost"
              size="tiny"
              value="<"
              isIconOnly={true}
              iconLeft="caret-left"
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
            />
            <Select
              selectedValue={table.getState().pagination.pageSize.toString()}
              label="Page Size"
              isLabelHidden={true}
              items={PAGINATION_OPTIONS}
              onChange={(e) => {
                table.setPageSize(Number(e.target.value));
              }}
            />
            <Button
              intent="ghost"
              size="tiny"
              value=">"
              isIconOnly={true}
              iconRight="caret-right"
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
            />
            <Button
              intent="ghost"
              size="tiny"
              isIconOnly={true}
              value=">>"
              iconLeft="caret-double-right"
              onClick={() => table.lastPage()}
              isDisabled={!table.getCanNextPage()}
            />
          </ButtonGroup>
          <Flex xAlign="center">
            <Text type="micro" color="secondary">
              {`${rowRange} of ${questionnaireCount}`}
            </Text>
          </Flex>
        </Flex>
      </CardPagination>
    </>
  );
};
